import { array, intersect, literal, number, partial, string, struct, union } from 'io-ts/Decoder';

import { iotsUtils } from '@bedc/core';

import { WholeHouseId as WholeHouseAncillaryItemId } from '../../../../job/ancillary-item';
import { Id as OrganizationId } from '../../../../organization/Id';
import { Id as MaterialCategoryId } from '../../../../whole-house/material-category/Id';
import { Id as MaterialTemplateId } from '../../../../whole-house/material-template/Id';
import { PriceUnitItem } from '../../../../whole-house/material/price-unit';
import { Id as PropertyType } from '../../../../whole-house/material/property-type';
import { PropertyItem } from '../../../../whole-house/material/property-type';
import { Id as StockStatus } from '../../../../whole-house/material/stock-status';
import { Id as UsageId } from '../../../../whole-house/usage/Id';
import { Request as AdditionalParameterCreateRequest } from '../../additionalparameters/create';

// ======================================================================
// Reuse mappings (no request object needed)
// ======================================================================

// `reuse` means "point at this entity that already exists in the target org", so a
// blank targetId is not a valid request: it lands in the commit's id map as an empty
// string, which then reads as a *missing* entry from an unrelated later stage.
const ReuseMapping = struct({
	sourceId: string,
	targetId: iotsUtils.NotEmptyString,
	action: literal('reuse'),
	entityType: union(
		literal('usage'),
		literal('material-template'),
		literal('material'),
		literal('material-category'),
		literal('formula'),
		literal('additional-parameter')
	),
});

// ======================================================================
// Create mappings by entity type
// ======================================================================

const CreateAdditionalParameterMapping = struct({
	sourceId: string,
	targetId: string,
	action: literal('create'),
	entityType: literal('additional-parameter'),
	request: AdditionalParameterCreateRequest,
});

const CreateFormulaMapping = struct({
	sourceId: string,
	targetId: string,
	action: literal('create'),
	entityType: literal('formula'),
	request: struct({
		name: iotsUtils.NotEmptyString,
		formula: iotsUtils.NotEmptyString,
		organizationId: OrganizationId,
	}),
	sourceParameterIds: array(string),
});

const CreateUsageMapping = struct({
	sourceId: string,
	targetId: string,
	action: literal('create'),
	entityType: literal('usage'),
	request: struct({
		name: iotsUtils.NotEmptyString,
		takeoffType: WholeHouseAncillaryItemId,
		organizationId: OrganizationId,
		wastage: number,
	}),
	sourceFormulaIds: array(string),
});

const UpdateUsageMapping = struct({
	sourceId: string,
	targetId: iotsUtils.NotEmptyString,
	action: literal('update'),
	entityType: literal('usage'),
	request: struct({
		name: iotsUtils.NotEmptyString,
		takeoffType: WholeHouseAncillaryItemId,
		wastage: number,
	}),
	sourceFormulaIds: array(string),
});

const CreateMaterialTemplateMapping = struct({
	sourceId: string,
	targetId: string,
	action: literal('create'),
	entityType: literal('material-template'),
	request: intersect(
		struct({
			organizationId: OrganizationId,
			materialTemplateRef: iotsUtils.NotEmptyString,
			propertyTypes: array(PropertyType),
		})
	)(
		partial({
			usages: array(UsageId),
		})
	),
	sourceUsageIds: array(string),
});

const UpdateMaterialTemplateMapping = struct({
	sourceId: string,
	targetId: iotsUtils.NotEmptyString,
	action: literal('update'),
	entityType: literal('material-template'),
	request: intersect(
		struct({
			propertyTypes: array(PropertyType),
		})
	)(
		partial({
			materialTemplateRef: iotsUtils.NotEmptyString,
			usages: array(UsageId),
		})
	),
	sourceUsageIds: array(string),
});

const CreateMaterialMapping = intersect(
	struct({
		sourceId: string,
		targetId: string,
		action: literal('create'),
		entityType: literal('material'),
		request: intersect(
			struct({
				name: iotsUtils.NotEmptyString,
				organizationId: OrganizationId,
				sku: iotsUtils.NotEmptyString,
				priceUnit: PriceUnitItem,
				stockStatus: StockStatus,
				propertyTypes: array(PropertyType),
				propertyValues: PropertyItem,
			})
		)(
			partial({
				materialCategoryId: MaterialCategoryId,
				newCategory: string,
				materialTemplateId: MaterialTemplateId,
			})
		),
	})
)(
	partial({
		sourceMaterialTemplateId: string,
		sourceCategoryId: string,
	})
);

// ======================================================================
// Create-caused-by-conflict mappings
// ======================================================================

const ConflictAdditionalParameterMapping = intersect(
	struct({
		sourceId: string,
		targetId: string,
		action: literal('create-caused-by-conflict'),
		entityType: literal('additional-parameter'),
		parameterRef: string,
		conflictType: literal('parameter-type-mismatched'),
		message: string,
		request: AdditionalParameterCreateRequest,
	})
)(
	partial({
		conflictingTargetId: string,
	})
);

const ConflictMaterialTemplateMapping = intersect(
	struct({
		sourceId: string,
		targetId: string,
		action: literal('create-caused-by-conflict'),
		entityType: literal('material-template'),
		materialTemplateRef: string,
		conflictType: literal('property-type-mismatched'),
		message: string,
		request: intersect(
			struct({
				organizationId: OrganizationId,
				materialTemplateRef: iotsUtils.NotEmptyString,
				propertyTypes: array(PropertyType),
			})
		)(
			partial({
				usages: array(UsageId),
			})
		),
		sourceUsageIds: array(string),
	})
)(
	partial({
		conflictingTargetId: string,
	})
);

const ConflictMaterialMapping = intersect(
	struct({
		sourceId: string,
		targetId: string,
		action: literal('create-caused-by-conflict'),
		entityType: literal('material'),
		materialSku: string,
		conflictType: literal('property-type-mismatched'),
		message: string,
		request: intersect(
			struct({
				name: iotsUtils.NotEmptyString,
				organizationId: OrganizationId,
				sku: iotsUtils.NotEmptyString,
				priceUnit: PriceUnitItem,
				stockStatus: StockStatus,
				propertyTypes: array(PropertyType),
				propertyValues: PropertyItem,
			})
		)(
			partial({
				materialCategoryId: MaterialCategoryId,
				newCategory: string,
				materialTemplateId: MaterialTemplateId,
			})
		),
	})
)(
	partial({
		sourceMaterialTemplateId: string,
		sourceCategoryId: string,
		conflictingTargetId: string,
	})
);

// ======================================================================
// Discriminated union of all mappings
// ======================================================================

const EntityMapping = union(
	ReuseMapping,
	CreateAdditionalParameterMapping,
	CreateFormulaMapping,
	CreateUsageMapping,
	UpdateUsageMapping,
	CreateMaterialTemplateMapping,
	UpdateMaterialTemplateMapping,
	CreateMaterialMapping,
	ConflictAdditionalParameterMapping,
	ConflictMaterialTemplateMapping,
	ConflictMaterialMapping
);

export const Request = intersect(
	struct({
		targetOrganizationId: OrganizationId,
	})
)(
	partial({
		mappings: array(EntityMapping),
		mode: union(literal('auto'), literal('create-all')),
	})
);
